import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { currentLang } from "../i18n";
import { LT_ROUTES } from "../data";
import BrandMark from "./BrandMark";

const LANGS = [
  { code: "en", name: "English" },
  { code: "de", name: "Deutsch" },
  { code: "fr", name: "Français" },
  { code: "it", name: "Italiano" },
  { code: "rm", name: "Rumantsch" },
];

const NAV_ITEMS = LT_ROUTES.filter((r) => r.nav);

function Navigation({ theme, onThemeToggle }) {
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const langRef = useRef(null);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = currentLang(i18n);

  useEffect(() => {
    const onDoc = (e) => {
      if (langRef.current && !langRef.current.contains(e.target))
        setLangOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // The sheet behind the open menu must not scroll, and focus moves onto
  // the menu so keyboard and screen-reader users land where the action is.
  const closeRef = useRef(null);
  const menuBtnRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (closeRef.current) closeRef.current.focus();
    const opener = menuBtnRef.current;
    return () => {
      document.body.style.overflow = prev;
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setMenuOpen(false);
      setLangOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // The button names the sheet you would switch to.
  const otherSheet = theme === "dark" ? t("theme.paper") : t("theme.blueprint");

  return (
    <nav className="nav">
      <div className="nav-inner">
        <NavLink to="/" className="brand" aria-label="LTplus AG">
          <BrandMark theme={theme} className="brand-logo" />
        </NavLink>

        <div className="nav-links">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              {t(item.key)}
            </NavLink>
          ))}
        </div>

        <div className="nav-aux">
          <button className="txt-btn" onClick={onThemeToggle}>
            {otherSheet}
          </button>

          <div className="lang" ref={langRef}>
            <button
              className="txt-btn"
              aria-expanded={langOpen}
              onClick={(e) => {
                e.stopPropagation();
                setLangOpen(!langOpen);
              }}
            >
              {lang.toUpperCase()}
            </button>
            {langOpen && (
              <div className="lang-list">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    aria-current={l.code === lang}
                    onClick={() => {
                      i18n.changeLanguage(l.code);
                      setLangOpen(false);
                    }}
                  >
                    <span>{l.name}</span>
                    <span className="code">{l.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            ref={menuBtnRef}
            className="txt-btn menu-btn"
            onClick={() => setMenuOpen(true)}
            aria-expanded={menuOpen}
          >
            {t("navigation.menu")}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="mobile-menu">
          <div className="mm-top">
            <BrandMark theme={theme} className="brand-logo" />
            <button
              ref={closeRef}
              className="txt-btn"
              onClick={() => setMenuOpen(false)}
            >
              {t("navigation.close")}
            </button>
          </div>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={() => setMenuOpen(false)}
            >
              {t(item.key)}
            </NavLink>
          ))}
        </div>
      )}
    </nav>
  );
}

export default Navigation;
